import DocsLayout, { CodeBlock, Callout } from '../../components/docs/DocsLayout';

export default function DocsQuickStart() {
  return (
    <DocsLayout 
      title="Quick Start" 
      description="Get your first AI agent up and running in minutes"
    >
      <section className="space-y-6">
        <p className="text-white/70 leading-relaxed">
          This guide will walk you through creating your first AI agent on AIBL. By the end, you'll have a fully functional agent with a smart account wallet ready to interact with DeFi protocols.
        </p>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Prerequisites</h2>
        
        <ul className="space-y-2 text-white/70">
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">✓</span>
            A wallet with some ETH for gas fees
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">✓</span>
            An AIBL account (create one at the dashboard)
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-400 mt-1">✓</span>
            Basic understanding of blockchain transactions
          </li>
        </ul>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Step 1: Create Your Account</h2>
        
        <p className="text-white/70">
          Navigate to the AIBL dashboard and connect your wallet or sign in with Google. This creates your developer account which can manage multiple agents.
        </p>

        <Callout type="info">
          Your developer account is separate from your agent wallets. Think of it as the control plane while agents are the execution layer.
        </Callout>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Step 2: Create an Agent</h2>
        
        <p className="text-white/70">
          From the dashboard, click "Create Agent" and provide a name. AIBL will automatically deploy an ERC-4337 smart account for your agent.
        </p>

        <CodeBlock language="json">
{`{
  "name": "TradingBot",
  "chain": "base",
  "wallet_type": "SMART_ACCOUNT"
}`}
        </CodeBlock>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Step 3: Configure Plugins</h2>
        
        <p className="text-white/70">
          Enable the plugins your agent needs. By default, all plugins are disabled for security. Navigate to the Plugins page to enable:
        </p>

        <ul className="space-y-2 text-white/70 mt-4">
          <li><strong className="text-white">Swap</strong> - Execute token swaps on supported DEXs</li>
          <li><strong className="text-white">Transfer</strong> - Send tokens to whitelisted addresses</li>
          <li><strong className="text-white">Bridge</strong> - Move assets across chains</li>
          <li><strong className="text-white">Wrap</strong> - Wrap and unwrap native tokens</li>
        </ul>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Step 4: Fund Your Agent</h2>
        
        <p className="text-white/70">
          Send some ETH to your agent's wallet address. This will be used for gas fees and operations. You can find the address on the agent detail page.
        </p>

        <Callout type="warning" title="Important">
          Start with a small amount for testing. You can always add more funds later once you've verified everything works correctly.
        </Callout>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Step 5: Make Your First API Call</h2>
        
        <p className="text-white/70">
          Use your agent's API key to execute operations. Here's an example swap request:
        </p>

        <CodeBlock language="bash">
{`curl -X POST https://api.aibl.dev/v1/agents/{agentId}/swap \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "tokenIn": "0x...",
    "tokenOut": "0x...",
    "amountIn": "1000000000000000000"
  }'`}
        </CodeBlock>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Next Steps</h2>
        
        <ul className="space-y-2 text-white/70">
          <li>• Learn about <a href="/docs/agents" className="text-blue-400 hover:underline">agent autonomy levels</a></li>
          <li>• Configure <a href="/docs/multisig" className="text-blue-400 hover:underline">multi-sig security</a> for high-value operations</li>
          <li>• Explore the full <a href="/docs/api/endpoints" className="text-blue-400 hover:underline">API reference</a></li>
        </ul>
      </section>
    </DocsLayout>
  );
}
